import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addSelectedFilterItemAction, removeSelectedFilterItemAction, selectEnabledInventoryAvailabilityStatusFilterItems, selectSelectedFilterItems, INVENTORY_AVAILABILITY_STATUS_FILTER_CATEGORY, MODEL_YEAR_FILTER_CATEGORY, TRIM_KEY_FILTER_CATEGORY, selectFilteredVehiclesByTrimAndModelYear, selectFilteredVehiclesByModelYear, selectFilteredVehiclesByTrim, } from '../../../core/reducers/vehicleListFilters';
import { InventoryAvailabilityStatus } from '../../../core/apis/inventoryApi';
import useInventoryData from './useInventoryData';
import { getIntColorFromColorCombination, useAppSelector, getFilteredVehiclesByTrim, getFilteredVehiclesByModelYear, } from '../../../core/utils';
import { selectCurrentVehicle } from '../../../core/reducers/currentVehicle';
const vehicleHasSelectedAvailability = (vehicle, selectedAvailabilityStatusFilter) => {
    const interiorColor = getIntColorFromColorCombination(vehicle);
    if (!selectedAvailabilityStatusFilter)
        return (interiorColor === null || interiorColor === void 0 ? void 0 : interiorColor.inStock) || (interiorColor === null || interiorColor === void 0 ? void 0 : interiorColor.onItsWay);
    const [, availabilityStatus] = selectedAvailabilityStatusFilter.split('|');
    if (availabilityStatus === InventoryAvailabilityStatus.inStock)
        return interiorColor === null || interiorColor === void 0 ? void 0 : interiorColor.inStock;
    if (availabilityStatus === InventoryAvailabilityStatus.onItsWay)
        return interiorColor === null || interiorColor === void 0 ? void 0 : interiorColor.onItsWay;
};
const isAvailabilityOptionDisabled = (itemKey, selectedAvailabilityStatusFilter, filteredVehiclesByModelYearAndTrim) => {
    var _a;
    // either inStock or onItsWay, only one can be enabled
    if (!!selectedAvailabilityStatusFilter && selectedAvailabilityStatusFilter !== itemKey)
        return true;
    return !((_a = filteredVehiclesByModelYearAndTrim === null || filteredVehiclesByModelYearAndTrim === void 0 ? void 0 : filteredVehiclesByModelYearAndTrim.filter(vehicle => vehicleHasSelectedAvailability(vehicle, itemKey))) === null || _a === void 0 ? void 0 : _a.length);
};
const isYearOptionDisabled = (itemKey, selectedYearFilters, selectedAvailabilityStatusFilter, filteredVehiclesByTrim, currentVehicleModelYear) => {
    var _a;
    if (selectedYearFilters.includes(itemKey) || itemKey === `${MODEL_YEAR_FILTER_CATEGORY}|${currentVehicleModelYear}`)
        return false;
    return !((_a = getFilteredVehiclesByModelYear(filteredVehiclesByTrim === null || filteredVehiclesByTrim === void 0 ? void 0 : filteredVehiclesByTrim.filter(vehicle => vehicleHasSelectedAvailability(vehicle, selectedAvailabilityStatusFilter)), [itemKey])) === null || _a === void 0 ? void 0 : _a.length);
};
const isTrimOptionDisabled = (filterItemKey, selectedTrimFilters, selectedAvailabilityStatusFilter, filteredVehiclesByModelYear) => {
    var _a;
    if (selectedTrimFilters.includes(filterItemKey))
        return false;
    return !((_a = getFilteredVehiclesByTrim(filteredVehiclesByModelYear === null || filteredVehiclesByModelYear === void 0 ? void 0 : filteredVehiclesByModelYear.filter(vehicle => vehicleHasSelectedAvailability(vehicle, selectedAvailabilityStatusFilter)), [filterItemKey])) === null || _a === void 0 ? void 0 : _a.length);
};
const useInventoryFilterOptionsList = () => {
    const { t } = useTranslation();
    const { trims, modelYears, inventoryStatuses, hasInventory } = useInventoryData();
    const filteredVehiclesByTrimAndModelYear = useAppSelector(selectFilteredVehiclesByTrimAndModelYear);
    const filteredVehiclesByModelYear = useAppSelector(selectFilteredVehiclesByModelYear);
    const filteredVehiclesByTrim = useAppSelector(selectFilteredVehiclesByTrim);
    const { modelYear: currentVehicleModelYear } = useAppSelector(selectCurrentVehicle);
    const dispatch = useDispatch();
    const handlerClickFilterItem = (selected, key) => {
        if (!hasInventory)
            return;
        if (selected) {
            dispatch(removeSelectedFilterItemAction(key));
            return;
        }
        dispatch(addSelectedFilterItemAction(key));
    };
    const yearItems = modelYears.map(y => ({
        label: y,
        key: `${MODEL_YEAR_FILTER_CATEGORY}|${y}`,
        icon: {},
    }));
    const trimItems = trims.map(trim => ({
        label: trim.trimName,
        key: `${TRIM_KEY_FILTER_CATEGORY}|${trim.trimKeys.join(',')}`,
        icon: {},
    }));
    const availabilityItems = inventoryStatuses.map(inventoryStatus => ({
        label: inventoryStatus === InventoryAvailabilityStatus.inStock
            ? t('Shared.Common.inStockLabel')
            : t('Shared.Common.onItsWayLabel'),
        key: `${INVENTORY_AVAILABILITY_STATUS_FILTER_CATEGORY}|${inventoryStatus}`,
        icon: {},
    }));
    const selectedYearFilters = useSelector(selectSelectedFilterItems(yearItems.map(item => item.key)));
    const selectedTrimFilters = useSelector(selectSelectedFilterItems(trimItems.map(item => item.key)));
    const enabledAvailabilityFilters = useSelector(selectEnabledInventoryAvailabilityStatusFilterItems);
    const selectedAvailabilityFilter = useSelector(selectSelectedFilterItems(enabledAvailabilityFilters))[0];
    const getAvailabilityOptionItem = (item, selectedItem) => ({
        ...item,
        selected: selectedItem === item.key,
        disabled: !hasInventory ||
            (hasInventory && isAvailabilityOptionDisabled(item.key, selectedItem, filteredVehiclesByTrimAndModelYear)),
        handleClick: handlerClickFilterItem,
    });
    const getYearOptionItem = (item, selectedItems) => ({
        ...item,
        selected: selectedItems.includes(item.key),
        disabled: isYearOptionDisabled(item.key, selectedItems, selectedAvailabilityFilter, filteredVehiclesByTrim, currentVehicleModelYear),
        handleClick: handlerClickFilterItem,
    });
    const getTrimOptionItem = (item, selectedItems) => ({
        ...item,
        selected: selectedItems.includes(item.key),
        disabled: isTrimOptionDisabled(item.key, selectedItems, selectedAvailabilityFilter, filteredVehiclesByModelYear),
        handleClick: handlerClickFilterItem,
    });
    const availabilityOptions = availabilityItems.map(item => getAvailabilityOptionItem(item, selectedAvailabilityFilter || ''));
    const yearOptions = yearItems.map(item => getYearOptionItem(item, selectedYearFilters));
    const trimsOptions = trimItems.map(item => getTrimOptionItem(item, selectedTrimFilters));
    const filterOptionsList = [
        {
            title: t('Pages.Models.Exploration.filterAvailabilityLabel'),
            options: availabilityOptions,
            key: 'availability',
        },
        {
            title: t('Pages.Models.Exploration.filterYearLabel'),
            options: yearOptions,
            key: 'year',
        },
        {
            title: t('Pages.Models.Exploration.filterTrimsLabel'),
            options: trimsOptions,
            key: 'trims',
        },
    ];
    return filterOptionsList;
};
export default useInventoryFilterOptionsList;
